import React, { useState } from 'react';
import { Box, Button, Divider, HStack, Link, Text, Tooltip, VStack } from '@chakra-ui/react';
import { MoonStarIcon, SparklesIcon, ListChecksIcon, BookOpenIcon, ImagesIcon, QuoteIcon, PhoneIcon } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
import { brand, whatsappHref } from '../data/brand';
const navItems = [{
  label: 'Servicios',
  href: '#servicios',
  icon: SparklesIcon
}, {
  label: '¿Por qué consultarnos?',
  href: '#por-que',
  icon: ListChecksIcon
}, {
  label: 'Acerca de nosotros',
  href: '#nosotros',
  icon: BookOpenIcon
}, {
  label: 'Galería y equipo',
  href: '#galeria',
  icon: ImagesIcon
}, {
  label: 'Experiencias',
  href: '#experiencias',
  icon: QuoteIcon
}];
function Ripple({
  id
}: {
  id: number;
}) {
  return <Box key={id} position="absolute" inset={0} borderRadius="full" bg="gold.400" pointerEvents="none" animation="goldRipple 0.6s ease-out forwards" data-id="element-166" data-mp="3q7la0gcr2e4m" />;
}
export function SideNav() {
  const [ripples, setRipples] = useState<Record<string, number>>({});
  const fireRipple = (key: string) => {
    setRipples(current => ({
      ...current,
      [key]: Date.now()
    }));
  };
  return <>
      {/* Desktop: floating vertical capsule anchored to the right edge */}
      <VStack as="nav" aria-label="Navegación principal" display={{
      base: 'none',
      lg: 'flex'
    }} position="fixed" right={6} top="50%" transform="translateY(-50%)" zIndex={40} spacing={3} px={2.5} py={4} borderRadius="full" bg="rgba(13,13,13,0.72)" backdropFilter="blur(14px)" border="1px solid" borderColor="rgba(242,183,5,0.35)" boxShadow="0 18px 46px rgba(0,0,0,0.55)" data-id="element-167" data-mp="wyqmamcvpuxi">
        <Tooltip label="Templo de Ayuda Esotérica" placement="left" hasArrow bg="ink.700" color="gold.300" data-id="element-168" data-mp="23n71em4gbgn9">
          <Link href="#inicio" aria-label="Ir al inicio" position="relative" overflow="hidden" display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" bg="crimson.500" color="cream.50" onClick={() => fireRipple('logo')} _hover={{
          bg: 'crimson.400'
        }} data-id="element-169" data-mp="28f77yxf117st">
            <MoonStarIcon size={20} aria-hidden="true" data-id="element-170" data-mp="1zbtg6ld6uj1n" />
            {ripples.logo && <Ripple id={ripples.logo} data-id="element-171" data-mp="1zd4lipma7yuv" />}
          </Link>
        </Tooltip>

        <Divider borderColor="rgba(242,183,5,0.3)" data-id="element-172" data-mp="1uja2loei3nwu" />

        {navItems.map(item => <Tooltip key={item.href} label={item.label} placement="left" hasArrow bg="ink.700" color="gold.300" openDelay={80} data-id="element-173" data-mp="1qjhq50lzkxm7">
            <Link href={item.href} aria-label={item.label} position="relative" overflow="hidden" display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" color="cream.100" border="1px solid" borderColor="transparent" onClick={() => fireRipple(item.href)} transition="all 0.22s ease" _hover={{
          color: 'gold.300',
          borderColor: 'rgba(242,183,5,0.5)',
          bg: 'rgba(242,183,5,0.1)'
        }} data-id="element-174" data-mp="1gmwj69qepcfq">
              <item.icon size={19} aria-hidden="true" data-id="element-175" data-mp="spqj9hgyl0hg" />
              {ripples[item.href] && <Ripple id={ripples[item.href]} data-id="element-176" data-mp="snm586pcil3g" />}
            </Link>
          </Tooltip>)}

        <Divider borderColor="rgba(242,183,5,0.3)" data-id="element-177" data-mp="xi9tc4gftdhi" />

        <Tooltip label={`Llamar ${brand.phone}`} placement="left" hasArrow bg="ink.700" color="gold.300" data-id="element-178" data-mp="3w54e8hguty5g">
          <Link href={brand.phoneHref} aria-label={`Llamar al ${brand.phone}`} display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" bg="gold.400" color="ink.900" _hover={{
          bg: 'gold.300'
        }} data-id="element-179" data-mp="4yl3t3exka7o">
            <PhoneIcon size={19} aria-hidden="true" data-id="element-180" data-mp="2bnv9927o9app" />
          </Link>
        </Tooltip>

        <Tooltip label="Escribir por WhatsApp" placement="left" hasArrow bg="ink.700" color="gold.300" data-id="element-181" data-mp="2pf07q0zazwyg">
          <Link href={whatsappHref()} isExternal aria-label="Escribir por WhatsApp" display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" bg="wa.500" color="ink.900" _hover={{
          bg: 'wa.600'
        }} data-id="element-182" data-mp="2leptk11cy08i">
            <WhatsAppIcon size={21} data-id="element-183" data-mp="101eoyfff6vk1" />
          </Link>
        </Tooltip>
      </VStack>

      {/* Mobile / tablet: fixed bottom bar with the two real contact actions */}
      <Box position="fixed" bottom={0} left={0} right={0} zIndex={40} display={{
      base: 'block',
      lg: 'none'
    }} bg="rgba(13,13,13,0.94)" backdropFilter="blur(12px)" borderTop="1px solid" borderColor="rgba(242,183,5,0.35)" px={3} pt={3} pb="calc(0.75rem + env(safe-area-inset-bottom))" data-id="element-184" data-mp="3gn1txsqe144q">
        <HStack spacing={3} maxW="30rem" mx="auto" data-id="element-185" data-mp="2sztmb45hlizj">
          <Button as="a" href={brand.phoneHref} variant="gold" flex="1" h="3.25rem" fontSize="xs" leftIcon={<PhoneIcon size={17} aria-hidden="true" data-id="element-187" data-mp="2nxkuib4comz0" />} data-id="element-186" data-mp="2xxbpedwtp3zm">
            <VStack spacing={0} align="flex-start" data-id="element-188" data-mp="h7wrg8lg9t0k">
              <Text as="span" fontSize="0.7rem" letterSpacing="0.14em" data-id="element-189" data-mp="h97uu7azgyr5">
                LLAMAR
              </Text>
              <Text as="span" fontSize="0.62rem" fontWeight={500} letterSpacing="0.02em" data-id="element-190" data-mp="m681okfnq5jb">
                954-674-1691
              </Text>
            </VStack>
          </Button>
          <Button as="a" href={whatsappHref()} target="_blank" rel="noopener noreferrer" variant="whatsapp" flex="1" h="3.25rem" fontSize="xs" leftIcon={<WhatsAppIcon size={19} data-id="element-192" data-mp="13gbxpj5bn5uc" />} data-id="element-191" data-mp="fpzirobns8u9">
            <VStack spacing={0} align="flex-start" data-id="element-193" data-mp="13f0kyttwenr3">
              <Text as="span" fontSize="0.7rem" letterSpacing="0.14em" data-id="element-194" data-mp="yh8vs7kn1fql">
                WHATSAPP
              </Text>
              <Text as="span" fontSize="0.62rem" fontWeight={500} letterSpacing="0.02em" data-id="element-195" data-mp="tq0ll9bhhqcd">
                954-658-7182
              </Text>
            </VStack>
          </Button>
        </HStack>
      </Box>
    </>;
}